import { useEffect, useState } from "react";
import {
  Sun,
  Cloud,
  CloudRain,
  CloudSun,
  CloudLightning,
  Snowflake,
  CloudFog,
  Wind,
} from "lucide-react";

function iconFor(code) {
  if (code == null) return { Icon: Sun, label: "—" };
  if (code === 0) return { Icon: Sun, label: "Sereno" };
  if ([1, 2].includes(code)) return { Icon: CloudSun, label: "Poco nuvoloso" };
  if (code === 3) return { Icon: Cloud, label: "Nuvoloso" };
  if ([45, 48].includes(code)) return { Icon: CloudFog, label: "Nebbia" };
  if ([51, 53, 55, 61, 63, 65, 80, 81, 82].includes(code))
    return { Icon: CloudRain, label: "Pioggia" };
  if ([71, 73, 75, 77, 85, 86].includes(code))
    return { Icon: Snowflake, label: "Neve" };
  if ([95, 96, 99].includes(code))
    return { Icon: CloudLightning, label: "Temporale" };
  return { Icon: Wind, label: "Vento" };
}

// Shared cache — coordinate arrotondate a 1 decimale (~11 km) sono sufficienti per l'Elba
const cache = new Map(); // key: `${lat1}|${lng1}` -> Promise<Map<date, data>>

function keyFor(lat, lng) {
  return `${Math.round(lat * 10) / 10}|${Math.round(lng * 10) / 10}`;
}

async function fetchBatch(lat, lng) {
  const k = keyFor(lat, lng);
  if (cache.has(k)) return cache.get(k);
  const promise = (async () => {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&daily=temperature_2m_max,temperature_2m_min,weather_code,wind_speed_10m_max&timezone=Europe%2FRome&forecast_days=16`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`meteo ${res.status}`);
    const json = await res.json();
    const m = new Map();
    if (json.daily && json.daily.time) {
      json.daily.time.forEach((date, i) => {
        m.set(date, {
          code: json.daily.weather_code[i],
          tmax: Math.round(json.daily.temperature_2m_max[i]),
          tmin: Math.round(json.daily.temperature_2m_min[i]),
          wind: Math.round(json.daily.wind_speed_10m_max[i]),
        });
      });
    }
    return m;
  })();
  cache.set(k, promise);
  // On error remove so it can be retried next time
  promise.catch(() => cache.delete(k));
  return promise;
}

export default function WeatherWidget({ lat, lng, date, compact = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const batch = await fetchBatch(lat, lng);
        if (cancelled) return;
        const d = batch.get(date);
        if (d) setData(d);
        else setError(true);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lat, lng, date]);

  if (error) {
    const fallback = { code: 0, tmax: 30, tmin: 22, wind: 12 };
    const { Icon, label } = iconFor(fallback.code);
    return (
      <div
        data-testid="weather-widget-fallback"
        className={`inline-flex items-center gap-2 ${
          compact ? "text-sm" : "text-base"
        } text-[#5C7180]`}
      >
        <Icon className="w-5 h-5 text-[#D96C4A]" strokeWidth={1.5} />
        <span className="text-[#1D3A4C] font-medium">
          {fallback.tmax}° / {fallback.tmin}°
        </span>
        <span className="text-[#8A9BA8]">{label}</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        data-testid="weather-widget-loading"
        className={`inline-flex items-center gap-2 ${
          compact ? "text-sm" : "text-base"
        } text-[#8A9BA8]`}
      >
        <div className="w-5 h-5 rounded-full border-2 border-[#D96C4A]/30 border-t-[#D96C4A] animate-spin" />
        <span>Meteo…</span>
      </div>
    );
  }

  const { Icon, label } = iconFor(data.code);

  return (
    <div
      data-testid="weather-widget"
      className={`inline-flex items-center gap-3 ${
        compact ? "text-sm" : "text-base"
      }`}
    >
      <Icon className="w-6 h-6 text-[#D96C4A]" strokeWidth={1.5} />
      <div className="flex items-baseline gap-2">
        <span className="text-[#1D3A4C] font-semibold text-lg">
          {data.tmax}°
        </span>
        <span className="text-[#8A9BA8] text-sm">/ {data.tmin}°</span>
      </div>
      <div className="flex flex-col leading-tight">
        <span className="text-[#1D3A4C] text-xs">{label}</span>
        <span className="text-[#8A9BA8] text-xs">
          <Wind className="inline w-3 h-3 mr-1" />
          {data.wind} km/h
        </span>
      </div>
    </div>
  );
}
